/**
 * PDF Security Utility
 *
 * Validates uploaded PDF files to ensure they do not contain embedded scripts
 * or active content that could lead to Stored XSS attacks.
 *
 * Scans for dangerous PDF operators/keywords:
 *  /JavaScript  – JavaScript action
 *  /JS          – Short-form JavaScript reference in action dictionaries
 *  /OpenAction  – Action executed automatically when the document is opened
 *  /AA          – Additional-actions dictionary (auto-trigger events)
 *  /AcroForm    – Interactive form (can embed scripts)
 *  /RichMedia   – Rich media annotations (Flash / embedded active content)
 *  /Launch      – Launch action (can execute external programs)
 *  /EmbeddedFile – Embedded file streams
 *  /XFA         – XML Forms Architecture (can contain scripts)
 */

// PDF magic-byte header
const PDF_HEADER = '%PDF-';

// Patterns that indicate potentially malicious active content inside a PDF.
// Each entry is a plain string that will be checked with case-insensitive indexOf.
const MALICIOUS_PATTERNS = [
  '/JavaScript',
  '/JS ',
  '/JS(',
  '/JS<',
  '/JS\n',
  '/JS\r',
  '/RichMedia',
  '/Launch',
  '/EmbeddedFile',
  '/XFA',
];

const CONTEXTUAL_PATTERNS = ['/AcroForm', '/OpenAction', '/AA ', '/AA(', '/AA<', '/AA\n', '/AA\r'];
const SCRIPT_PATTERNS = ['/JavaScript', '/JS ', '/JS(', '/JS<', '/JS\n', '/JS\r'];

/**
 * Scans a PDF File object for malicious embedded scripts / active content.
 *
 * 1. Validates the file starts with the %PDF- magic bytes.
 * 2. Reads the entire file as latin1 (byte-transparent) and scans for
 *    dangerous PDF operators in 1 MB overlapping chunks so that even very
 *    large files can be handled without excessive peak memory.
 *
 * @param {File} file – The PDF file to scan.
 * @returns {Promise<void>} Resolves when the file is safe; rejects with a
 *          descriptive Error when malicious content is detected or the file
 *          is not a valid PDF.
 */
export const scanPdfSecurity = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const bytes = new Uint8Array(arrayBuffer);

        // --- 1. Validate PDF header (%PDF-) ---
        if (bytes.length < 5) {
          return reject(new Error('Upload blocked: File is too small to be a valid PDF.'));
        }

        const header = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3], bytes[4]);
        if (header !== PDF_HEADER) {
          return reject(new Error('Upload blocked: Invalid PDF file format.'));
        }

        const decoder = new TextDecoder('latin1');
        const CHUNK = 1024 * 1024;
        const OVERLAP = 256;

        let hasContextualPattern = false;
        let hasScriptPattern = false;

        for (let offset = 0; offset < bytes.length; offset += CHUNK) {
          const end = Math.min(offset + CHUNK + OVERLAP, bytes.length);
          const chunk = decoder.decode(bytes.slice(offset, end));

          for (let i = 0; i < MALICIOUS_PATTERNS.length; i++) {
            if (chunk.indexOf(MALICIOUS_PATTERNS[i]) !== -1) {
              return reject(
                new Error('Upload blocked: Unsafe PDF content detected.'),
              );
            }
          }

          if (!hasContextualPattern) {
            for (let i = 0; i < CONTEXTUAL_PATTERNS.length; i++) {
              if (chunk.indexOf(CONTEXTUAL_PATTERNS[i]) !== -1) {
                hasContextualPattern = true;
                break;
              }
            }
          }
          if (!hasScriptPattern) {
            for (let i = 0; i < SCRIPT_PATTERNS.length; i++) {
              if (chunk.indexOf(SCRIPT_PATTERNS[i]) !== -1) {
                hasScriptPattern = true;
                break;
              }
            }
          }
        }

        if (hasContextualPattern && hasScriptPattern) {
          return reject(
            new Error('Upload blocked: Unsafe PDF content detected.'),
          );
        }

        // File is safe
        resolve();
      } catch (err) {
        reject(new Error('Upload blocked: Unable to scan PDF file for security threats.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Upload blocked: Unable to read PDF file.'));
    };

    reader.readAsArrayBuffer(file);
  });
};
